import './HeroStatsCell.scss';
import { memo, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { useLazyQuery } from '@apollo/client';

import { Button, Icons, notify, Spinner, Tooltip } from 'plume-ui';

import { Maybe } from '../../../../types';
import {
  AdjustableComponent,
  useClasses,
} from '../../../../utils/hooks/useClasses';
import { getQuerySuppressedLocations } from '../../services/healthcheckQueries';
import { activePartnerAtom } from '../../../../state';

const STANDARD_DATE_TIME_FORMAT = 'YYYY-MM-DD_HH-mm-ss';

export type HeroStatsCellProps = {
  label: Maybe<string>;
  value: Maybe<number>;
  secondaryValue?: Maybe<number>;
  unit?: string;
  status?: string;
  trend?: number;
  cellSize?: string;
  ready?: boolean;
  valueId?: string;
  tooltipText?: string;
  undefinedText?: string;
  id?: string;
};

type HeroStatsCellStyles = {
  root: string;
  MonitorName: string;
  MonitorValue: string;
};

const HeroStatsCell: AdjustableComponent<
  HeroStatsCellProps,
  HeroStatsCellStyles
> = ({
  label,
  ready,
  value,
  secondaryValue,
  unit,
  valueId,
  classes,
  tooltipText,
  undefinedText,
  id,
}) => {
  const { t } = useTranslation();
  const [downloaded, setDownloaded] = useState(false);
  const styles = useClasses(
    {
      root: 'HeroStatsCell',
      MonitorName: 'HeroStatsCell__MonitorName',
      MonitorValue: 'HeroStatsCell__MonitorValue',
    },
    classes,
  );

  const activePartner = useRecoilValue(activePartnerAtom);

  const [getSuppressedLocations, { loading }] = useLazyQuery(
    getQuerySuppressedLocations(),
    {
      fetchPolicy: 'network-only',
    },
  );

  const onDownloadSuppressedLocations = async () => {
    try {
      const data = await getSuppressedLocations({
        variables: {
          partnerId: activePartner?.id || '',
        },
      });
      const locations = data?.data?.result;

      if (!locations) return;

      const csvData = locations.map((location) => ({
        location_id: location.location_id,
        account_id: location.account_id,
        customer_id: location.customer_id,
        n_days_offline: location.n_days_offline,
        offline_since: location.offline_since,
      }));

      const csvContent = [
        [
          t('healthSummary.hero.suppressedOfflineLocations.locationId'),
          t('healthSummary.hero.suppressedOfflineLocations.accountId'),
          t('healthSummary.hero.suppressedOfflineLocations.customerId'),
          t('healthSummary.hero.suppressedOfflineLocations.daysOffline'),
          t('healthSummary.hero.suppressedOfflineLocations.offlineSince'),
        ],
        ...csvData.map((row) => [
          row.location_id,
          row.account_id,
          row.customer_id,
          row.n_days_offline,
          row.offline_since,
        ]),
      ];

      const csvContentString = csvContent
        .map((row) => row.join(','))
        .join('\n');

      const blob = new Blob([csvContentString], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dayjs().format(
        STANDARD_DATE_TIME_FORMAT,
      )}_suppressed_locations.csv`;
      a.click();
      setDownloaded(true);
      setTimeout(() => {
        URL.revokeObjectURL(url);
        setDownloaded(false);
      }, 2000);
    } catch (error) {
      console.error('Error downloading suppressed locations:', error);
      notify({
        type: 'error',
        title: t('error'),
        body: error instanceof Error ? error.message : t('genericErrorMessage'),
      });
    }
  };

  if (ready) {
    return (
      <span className="HeroStatsCell" data-testid="hero-stats-cell">
        <div className={styles.MonitorValue}>
          <div id={valueId || label}>
            {value !== undefined && value !== null
              ? `${Math.round(value).toLocaleString()}${unit || ''}`
              : undefinedText || '\u2014'}
          </div>
          <div id={valueId || label}>
            {secondaryValue !== undefined
              ? Math.round(secondaryValue).toLocaleString()
              : ''}
          </div>
          {id?.includes('suppressed') && (
            <Button
              classes={(current) => ({
                ...current,
                container: `${current.root} HeroStatsCell__button`,
              })}
              styleVariant="tertiary"
              icon={<Icons.DownloadIcon width={18} />}
              loading={loading}
              onClick={onDownloadSuppressedLocations}
              disabled={downloaded}
            >
              {t('download')}
            </Button>
          )}
        </div>
        <div className={styles.MonitorName}>
          {label && <>{t(label)}</>}
          {tooltipText !== undefined && (
            <Tooltip
              position="below"
              label={tooltipText}
              overrideMaxCharacters
              openInPortal
              classes={(current) => ({
                ...current,
                root: `${current.root} HeroStatsCell__tooltip`,
              })}
            >
              <Icons.InfoIcon width={12} className="HealthSummary__InfoIcon" />
            </Tooltip>
          )}
        </div>
      </span>
    );
  }

  return (
    <span
      className="HeroStatsCell SubscriberStatsRow__loading"
      data-testid="hero-stats-cell"
    >
      <div
        style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}
      >
        {t('loading')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Spinner size="small" visible />
      </div>
    </span>
  );
};

export default memo(HeroStatsCell);
